/**
 * How the income tax calculator works out each country's tax for the 2026 tax year. The figures (bands, rates,
 * thresholds) come from the database (Admin > Voucha Calculators; voucha_tax_brackets / _regions / _settings), so
 * a new tax year only needs new figures there. Brackets are on taxable income, after the allowances below.
 *
 *   Nigeria  Nigeria Tax Act 2025: pension (8% of salary, optional) and rent relief (20% of rent, capped) come off
 *            first; then the PAYE bands
 *   US       standard deduction (single filer), federal brackets, Social Security and Medicare; state tax is a flat
 *            approximate rate
 *   Canada   federal brackets less the basic personal amount, CPP and EI credits at the lowest rate; CPP (and CPP2)
 *            and EI; provincial tax is a flat approximate rate
 *   UK       personal allowance (tapered above £100,000), UK or Scottish bands, employee National Insurance
 */

export function progressive(income, brackets) {
  let tax = 0;
  for (const b of brackets || []) {
    if (income > b.min) tax += (Math.min(income, b.max) - b.min) * b.rate;
    if (income <= b.max) break;
  }
  return tax;
}
const n = function (v, d) {
  if (d === void 0) {
    d = 0;
  }
  return typeof v === 'number' && isFinite(v) ? v : Number(v) || d;
};

/**
 * cfg: one country's config; income: total yearly income (salary + bonus + commission); salary: yearly salary only;
 * opts: { pension: bool, rent: number } (Nigeria). Returns { lines: [{key, label, value}], total, taxable, details }.
 */
export function calculateTax(cfg, income, salary, regionCode, opts) {
  if (opts === void 0) {
    opts = {};
  }
  const s = cfg.settings || {};
  const region = (cfg.regions || []).find(r => r.code === regionCode) || {
    rate: 0,
    code: regionCode
  };
  const lines = [];
  const add = (key, label, value) => {
    if (value > 0.005) lines.push({
      key,
      label,
      value
    });
  };
  let taxable = income;
  const details = {};
  if (cfg.code === 'NG') {
    const pension = opts.pension === false ? 0 : salary * n(s.pensionRate, 0.08);
    const relief = Math.min(Math.max(0, n(opts.rent)) * n(s.rentReliefRate, 0.2), n(s.rentReliefCap, 500000));
    taxable = Math.max(0, income - pension - relief);
    Object.assign(details, {
      pension,
      rentRelief: relief
    });
    add('payeTax', 'PAYE tax', progressive(taxable, cfg.brackets));
    add('pension', 'Pension contribution', pension);
  } else if (cfg.code === 'US') {
    taxable = Math.max(0, income - n(s.standardDeduction));
    details.standardDeduction = Math.min(income, n(s.standardDeduction));
    add('federalTax', 'Federal income tax', progressive(taxable, cfg.brackets));
    add('stateTax', 'State income tax (approx.)', income * n(region.rate));
    add('socialSecurity', 'Social Security', Math.min(income, n(s.ssMax, Infinity)) * n(s.socialSecurity));
    const medicare = income * n(s.medicare) + Math.max(0, income - n(s.medicareThreshold, Infinity)) * n(s.additionalMedicare);
    add('medicare', 'Medicare', medicare);
  } else if (cfg.code === 'CA') {
    const ympe = n(s.ympe, n(s.cppMax));
    const cppEarnings = Math.max(0, Math.min(income, ympe) - n(s.cppExemption));
    const cpp = cppEarnings * n(s.cpp);
    const cppBase = cppEarnings * n(s.cppBase, n(s.cpp));
    const cpp2 = Math.max(0, Math.min(income, n(s.yampe, ympe)) - ympe) * n(s.cpp2);
    const ei = Math.min(income, n(s.eiMax)) * n(s.ei);
    // the enhanced part of CPP (and CPP2) is deducted from income; the base part and EI earn a tax credit
    taxable = Math.max(0, income - (cpp - cppBase) - cpp2);
    const start = n(s.bpaPhaseStart, Infinity);
    const end = n(s.bpaPhaseEnd, Infinity);
    const bpaMax = n(s.bpaMax);
    const bpaMin = n(s.bpaMin, bpaMax);
    const bpa = income <= start ? bpaMax : income >= end ? bpaMin : bpaMax - (bpaMax - bpaMin) * (income - start) / (end - start);
    const lowest = n(s.lowestRate, cfg.brackets && cfg.brackets[0] && cfg.brackets[0].rate || 0.15);
    const credits = lowest * (bpa + cppBase + ei);
    Object.assign(details, {
      basicPersonalAmount: bpa,
      credits
    });
    add('federalTax', 'Federal income tax', Math.max(0, progressive(taxable, cfg.brackets) - credits));
    add('provincialTax', 'Provincial income tax (approx.)', taxable * n(region.rate));
    add('cpp', 'CPP contributions', cpp + cpp2);
    add('ei', 'EI premiums', ei);
  } else if (cfg.code === 'GB') {
    const pa = Math.max(0, n(s.personalAllowance, 12570) - Math.max(0, income - n(s.taperStart, 100000)) / 2);
    taxable = Math.max(0, income - pa);
    details.personalAllowance = pa;
    const scottish = region.code === 'SCT' && Array.isArray(s.scotland) && s.scotland.length;
    const bands = scottish ? s.scotland.map(b => ({
      min: n(b[0]),
      max: b[1] === null ? Infinity : n(b[1], Infinity),
      rate: n(b[2])
    })) : cfg.brackets;
    add('incomeTax', scottish ? 'Income tax (Scottish rates)' : 'Income tax', progressive(taxable, bands) + taxable * n(scottish ? 0 : region.rate));
    const lower = n(s.niThreshold, 12570);
    const upper = n(s.niUpperLimit, 50270);
    const ni = Math.max(0, Math.min(income, upper) - lower) * n(s.ni) + Math.max(0, income - upper) * n(s.niReduced);
    add('ni', 'National Insurance', ni);
  }
  const total = lines.reduce((t, l) => t + l.value, 0);
  return {
    lines,
    total,
    taxable,
    details
  };
}